import React, { useEffect, useState } from 'react';
import { Box, IconButton, SvgIcon, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { borderRadius, boxShadows, palette, spaces } from '../../../../../assets/styles/themes/tokens';
import getMuiIcon from '../../../../../utils/getMuiIcon';
import { copyToClipboard } from '../../../../../utils';
import { notifyBugsnagHandledError } from '../../../../../utils/bugsnagClient';
import { downloadImage } from '../../utils';

type Props = {
  src?: string;
  alt?: string;
  onClick?: () => void;
  onCopy?: () => void;
  onDownload?: () => void;
};

const BUTTON_SIZE = 36;
const ICON_SIZE = 20;
const COPIED_FEEDBACK_MS = 3000;

const actionButtonStyles = {
  width: BUTTON_SIZE,
  height: BUTTON_SIZE,
  backgroundColor: palette.common.white,
  boxShadow: boxShadows.elevation3,
  color: palette.text.primary,
  '&:hover': {
    backgroundColor: palette.common.white,
    color: palette.primary.main,
  },
};

const ChatImage = React.forwardRef<HTMLDivElement, Props>(({ src, alt, onClick, onCopy, onDownload }, ref) => {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!isCopied) return;
    const timeout = setTimeout(() => setIsCopied(false), COPIED_FEEDBACK_MS);
    return () => clearTimeout(timeout);
  }, [isCopied]);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!src) return;
    onCopy?.();
    try {
      const res = await fetch(src, { mode: 'cors', cache: 'reload' });
      const blob = await res.blob();
      await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
    } catch (error) {
      notifyBugsnagHandledError(error as Error);
      try {
        copyToClipboard(src);
      } catch (fallbackError) {
        notifyBugsnagHandledError(fallbackError as Error);
      }
    }
    setIsCopied(true);
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!src) return;
    onDownload?.();
    downloadImage(src, alt);
  };

  const clickable = Boolean(onClick && src);

  return (
    <Box
      ref={ref}
      sx={{
        position: 'relative',
        display: 'block',
        width: 'fit-content',
        maxWidth: 'min(100%, 460px)',
        '&:hover .chat-image-actions': { opacity: 1 },
      }}
    >
      <Box
        component='img'
        src={src}
        alt={alt}
        onClick={clickable ? onClick : undefined}
        sx={{
          maxWidth: '100%',
          height: 'auto',
          display: 'block',
          borderRadius: borderRadius.md,
          ...(clickable ? { cursor: 'pointer' } : {}),
        }}
      />
      <Box
        className='chat-image-actions'
        sx={{
          position: 'absolute',
          top: spaces.sm,
          right: spaces.sm,
          display: 'flex',
          gap: spaces.xs,
          opacity: 0,
          transition: 'opacity 150ms',
          '@media (hover: none)': { display: 'none' },
        }}
      >
        <Tooltip title={t('Copy')} placement='top' arrow>
          <IconButton
            aria-label={t('Copy')}
            onClick={handleCopy}
            data-testid='chat-image-copy-button'
            sx={actionButtonStyles}
          >
            <SvgIcon
              sx={{ width: ICON_SIZE, height: ICON_SIZE }}
              viewBox='0 0 24 24'
              component={getMuiIcon(isCopied ? 'check' : 'copy')}
              aria-hidden='true'
            />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('Download')} placement='top' arrow>
          <IconButton
            aria-label={t('Download')}
            onClick={handleDownload}
            data-testid='chat-image-download-button'
            sx={actionButtonStyles}
          >
            <SvgIcon
              sx={{ width: ICON_SIZE, height: ICON_SIZE }}
              viewBox='0 0 24 24'
              component={getMuiIcon('download')}
              aria-hidden='true'
            />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
});

ChatImage.displayName = 'ChatImage';

export default ChatImage;
